import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { BrandLogo } from "../components/BrandLogo";
import { ArrowLeft, ArrowRight, Sunset, Coffee, Utensils, Palette, IceCream, ShieldCheck, CheckCircle2 } from "lucide-react";
import { HONOLULU_GUIDES } from "../lib/honoluluGuides";

const PAGE_URL = "https://datebid.org/honolulu-first-date-ideas";
const PAGE_TITLE = "Safe First Date Ideas in Honolulu | DateBid";
const PAGE_DESC = "Looking for first date ideas in Honolulu? Discover public, memorable spots across Oahu, from Ala Moana to Kakaako, plus tips for meeting safely.";
const OG_IMAGE = "https://datebid.org/brand/og-datebid.png";

const IDEAS = [
  { icon: Sunset, t: "Sunset by the water", d: "Few things set the mood like golden hour. A walk along Ala Moana Beach Park or the Waikiki shoreline gives you scenery, space to talk, and a natural end point. Go early enough to enjoy the light, and keep the plan short." },
  { icon: Coffee, t: "Coffee in Kaimuki or Kakaako", d: "A coffee date is low pressure and easy to extend. Both neighborhoods have independent cafes with relaxed, welcoming settings. If you're getting along, a walk or a bite afterward is easy to add." },
  { icon: Utensils, t: "A casual bite in Chinatown", d: "Downtown and Chinatown offer a wide range of restaurants and small bars. Pick somewhere with a lively crowd, share a few plates, and let the food start the conversation." },
  { icon: Palette, t: "A stroll through the arts", d: "Browsing a gallery, a market, or a First Friday evening gives you something to talk about besides yourselves. Check event times ahead of your date." },
  { icon: IceCream, t: "Dessert and a short walk", d: "If you'd rather keep it brief, meet for shave ice or dessert and take a short walk. Ending on a high note is better than dragging out a date that isn't clicking." },
];

const SAFETY = [
  "Meet in a public place, and arrange your own transport.",
  "Tell a friend where you'll be and when to expect you back.",
  "Keep your home address and financial details private.",
  "Trust your instincts. You can leave at any time.",
];

export default function HonoluluFirstDateIdeas() {
  useEffect(() => {
    document.title = PAGE_TITLE;
    const upsert = (selector, create) => {
      let el = document.head.querySelector(selector);
      if (!el) { el = create(); document.head.appendChild(el); }
      return el;
    };
    const setMetaName = (name, content) => {
      const el = upsert(`meta[name="${name}"]`, () => { const m = document.createElement("meta"); m.setAttribute("name", name); return m; });
      el.setAttribute("content", content);
    };
    const setMetaProp = (prop, content) => {
      const el = upsert(`meta[property="${prop}"]`, () => { const m = document.createElement("meta"); m.setAttribute("property", prop); return m; });
      el.setAttribute("content", content);
    };
    setMetaName("description", PAGE_DESC);
    const canonical = upsert('link[rel="canonical"]', () => { const l = document.createElement("link"); l.setAttribute("rel", "canonical"); return l; });
    canonical.setAttribute("href", PAGE_URL);
    setMetaProp("og:type", "article");
    setMetaProp("og:site_name", "DateBid");
    setMetaProp("og:url", PAGE_URL);
    setMetaProp("og:title", PAGE_TITLE);
    setMetaProp("og:description", PAGE_DESC);
    setMetaProp("og:image", OG_IMAGE);
    setMetaName("twitter:card", "summary_large_image");
    setMetaName("twitter:title", PAGE_TITLE);
    setMetaName("twitter:description", PAGE_DESC);
    setMetaName("twitter:image", OG_IMAGE);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "ld-honolulu-first-date-ideas";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": "Safe First Date Ideas in Honolulu",
      "description": PAGE_DESC,
      "mainEntityOfPage": { "@type": "WebPage", "@id": PAGE_URL },
      "image": OG_IMAGE,
      "author": { "@type": "Organization", "name": "DateBid" },
      "publisher": { "@type": "Organization", "name": "DateBid", "logo": { "@type": "ImageObject", "url": OG_IMAGE } },
    });
    const existing = document.getElementById("ld-honolulu-first-date-ideas");
    if (existing) existing.remove();
    document.head.appendChild(ld);

    return () => { const s = document.getElementById("ld-honolulu-first-date-ideas"); if (s) s.remove(); };
  }, []);

  return (
    <div className="db-grain min-h-screen relative" style={{ background: "#0C0A0A" }} data-testid="honolulu-ideas-page">
      <nav className="relative z-20 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/" data-testid="ideas-brand-logo" className="inline-flex items-center"><BrandLogo variant="wordmark" className="h-10 sm:h-12 w-auto" /></Link>
        <Link to="/" data-testid="ideas-back-link" className="text-sm text-[#D8D2C9] hover:text-[#FBF8F3] inline-flex items-center gap-1.5"><ArrowLeft size={15} /> Back to home</Link>
      </nav>

      <header className="relative z-10 px-6 sm:px-10 pt-6 pb-10 max-w-3xl mx-auto">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Honolulu · First Dates</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#FBF8F3] leading-tight">Safe First Date Ideas in Honolulu</h1>
        <p className="mt-5 text-[#D8D2C9] leading-relaxed">
          A good first date is simple: a public place, good company, and an easy way to leave if the spark isn't there. Honolulu is made for it. Here are our favorite ways to meet someone new across Oahu.
        </p>
      </header>

      <main className="relative z-10 px-6 sm:px-10 pb-20 max-w-3xl mx-auto space-y-10">
        {IDEAS.map((idea, i) => (
          <section key={i} data-testid={`idea-${i + 1}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-[#541224]/70 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] shrink-0"><idea.icon size={18} /></div>
              <h2 className="font-serif text-2xl text-[#FBF8F3]">{idea.t}</h2>
            </div>
            <p className="text-[#D8D2C9] text-sm leading-relaxed pl-1">{idea.d}</p>
          </section>
        ))}

        <section data-testid="ideas-safety" className="rounded-2xl border border-[#D4AF37]/20 bg-[#1a1113]/60 p-6 sm:p-7">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 rounded-full bg-[#541224]/70 border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] shrink-0"><ShieldCheck size={18} /></div>
            <h2 className="font-serif text-2xl text-[#FBF8F3]">Our first-date safety checklist</h2>
          </div>
          <ul className="space-y-2 text-sm text-[#D8D2C9] leading-relaxed">
            {SAFETY.map((s, i) => (
              <li key={i} className="flex gap-2"><CheckCircle2 size={16} className="text-[#D4AF37] mt-0.5 shrink-0" /><span>{s}</span></li>
            ))}
          </ul>
        </section>

        <section data-testid="ideas-cta" className="rounded-3xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#541224]/40 to-[#141113]/70 p-8 sm:p-10">
          <h2 className="font-serif text-2xl sm:text-3xl text-[#FBF8F3] leading-tight">Start with a real offer</h2>
          <p className="mt-4 text-[#D8D2C9] text-sm leading-relaxed">
            On DateBid, you can discover people across Honolulu, make a respectful date offer, and plan a public first meeting together. Offers are voluntary, and any amount is arranged directly between members. All members are 18+, identity verification is available, and you can report or block anyone.
          </p>
          <Link to="/auth?mode=register" data-testid="ideas-join-btn" className="db-offer-btn rounded-full px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2 mt-6">
            Join DateBid <ArrowRight size={16} />
          </Link>
        </section>

        <section data-testid="ideas-related" className="pt-6 border-t border-white/8">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] mb-4">Keep reading</div>
          <div className="space-y-2">
            {HONOLULU_GUIDES.filter((g) => g.path !== "/honolulu-first-date-ideas").map((g, i) => (
              <Link key={g.path} to={g.path} data-testid={`ideas-related-${i + 1}`} className="flex items-center gap-1.5 text-sm font-semibold text-[#D4AF37] hover:text-[#E6C468] transition-colors">{g.label} <ArrowRight size={14} /></Link>
            ))}
          </div>
        </section>
      </main>

      <footer className="relative z-10 px-6 sm:px-10 py-10 border-t border-white/8 text-center text-xs text-[#9E978E]">
        <p>DateBid · Meet in public places · Never share home addresses or financial details · Members must be 18+</p>
        <BrandLogo variant="wordmarkSm" className="h-9 w-auto mx-auto mt-3 opacity-90" />
      </footer>
    </div>
  );
}
